import { SEO_SITE } from '@src/configs/seo.config'
import { SeasonProductType } from '@schemas'

/**
 * schema.org 구조화 데이터 생성기.
 *
 * 검색 결과의 리치 스니펫(가격·재고·평점)과 AI 답변 인용은 둘 다 이걸 읽는다.
 * 예전에는 index.html 에 `OnlineStore` 하나만 있었고 제품 정보가 전혀 없었다.
 */

export interface ProductJsonLdParamsType {
	name: string
	description: string
	/** 라우트 경로. `/shop/kdm6` */
	path: string
	image: string | string[]
	/** 시즌 상품 정보. 없으면 offers 를 붙이지 않는다 (출시 전 제품) */
	product?: SeasonProductType
	/** 출시 전이라 가격이 없을 때 PreOrder 로 표시 */
	preOrder?: boolean
	sku?: string
}

/**
 * 재고 상태를 schema.org availability 로 옮긴다.
 *
 * SeasonProductType.status: not_start | expired | inactive | sold_out | active
 */
const toAvailability = (product: SeasonProductType): string => {
	if (product.soldOutYn === 1 || product.status === 'sold_out') {
		return 'https://schema.org/SoldOut'
	}

	if (product.status === 'not_start' || product.preorderYn === 1) {
		return 'https://schema.org/PreOrder'
	}

	if (product.status === 'active') {
		return 'https://schema.org/InStock'
	}

	return 'https://schema.org/OutOfStock'
}

export const buildProductJsonLd = (params: ProductJsonLdParamsType): object => {
	const url = `${SEO_SITE.origin}${params.path}`

	const res: { [key: string]: any } = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: params.name,
		description: params.description,
		url: url,
		image: params.image,
		brand: {
			'@type': 'Brand',
			name: SEO_SITE.siteName,
		},
	}

	if (params.sku !== undefined && params.sku !== '') {
		res.sku = params.sku
	}

	if (params.preOrder === true) {
		// 출시 전 — 가격을 확정할 수 없으므로 재고 상태만 알린다.
		res.offers = {
			'@type': 'Offer',
			url: url,
			availability: 'https://schema.org/PreOrder',
			priceCurrency: 'USD',
		}

		return res
	}

	// 가격이 0 이면 시즌 상품이 아직 안 실렸다는 뜻이라 offers 를 붙이지 않는다.
	// 잘못된 가격을 구조화 데이터로 내보내는 것보다 없는 편이 낫다.
	if (params.product !== undefined && params.product.seasonUsd > 0) {
		const price = params.product.seasonUsd - (params.product.seasonDiscountUsd ?? 0)

		res.offers = {
			'@type': 'Offer',
			url: url,
			priceCurrency: 'USD',
			price: price.toFixed(2),
			availability: toAvailability(params.product),
			seller: {
				'@type': 'Organization',
				name: SEO_SITE.siteName,
			},
		}

		if (params.product.seasonEndDate !== '') {
			res.offers.priceValidUntil = params.product.seasonEndDate.slice(0, 10)
		}
	}

	return res
}

/**
 * 사이트 전체 조직 정보. 홈에 넣는다.
 *
 * 예전에는 index.html 에 정적으로 박혀 있었다. 프리렌더가 이걸 정적 HTML 로
 * 만들어주므로 양쪽에 두면 같은 엔티티가 두 번 나온다.
 */
export const buildOnlineStoreJsonLd = (): object => ({
	'@context': 'https://schema.org',
	'@type': 'OnlineStore',
	name: SEO_SITE.siteName,
	url: SEO_SITE.origin,
	logo: SEO_SITE.defaultOgImage,
	description:
		'Builder of MIDI foot controllers and programmable loop switchers for guitar pedalboards.',
	sameAs: ['https://www.youtube.com/@judahs-io'],
	contactPoint: {
		'@type': 'ContactPoint',
		contactType: 'customer service',
		email: 'cs@judahs.io',
		availableLanguage: ['English', 'Korean'],
	},
})

export interface ItemListEntryType {
	name: string
	path: string
}

/** 라인업 목록. /shop 에 넣는다. */
export const buildItemListJsonLd = (entries: ItemListEntryType[]): object => ({
	'@context': 'https://schema.org',
	'@type': 'ItemList',
	itemListElement: entries.map((anEntry, idx) => ({
		'@type': 'ListItem',
		position: idx + 1,
		name: anEntry.name,
		url: `${SEO_SITE.origin}${anEntry.path}`,
	})),
})

export interface BreadcrumbEntryType {
	name: string
	path: string
}

export const buildBreadcrumbJsonLd = (entries: BreadcrumbEntryType[]): object => ({
	'@context': 'https://schema.org',
	'@type': 'BreadcrumbList',
	itemListElement: entries.map((anEntry, idx) => ({
		'@type': 'ListItem',
		position: idx + 1,
		name: anEntry.name,
		item: `${SEO_SITE.origin}${anEntry.path}`,
	})),
})
